import { useState, useEffect, Suspense } from 'react';
import { init, identify, Identify, add } from '@amplitude/analytics-browser';
import { Plugin } from '@amplitude/analytics-types';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '@fontsource/open-sauce-one';
import RootPage from './views/RootPage';
import ErrorBoundary from './components/ErrorBoundary';
import SimpleBackdrop from './components/Backdrop';
import AuthGuard from './components/AuthGuard';
import SuspenseBackdrop from './components/Backdrop/SuspenseBackdrop';
import { useAuth } from './hooks';
import {
  adminRoutesV2,
  standaloneRoutesV2,
  allAccessibleRoutesV2,
  companyUserRoutesV2,
  lenderRoutesV2,
  publicRoutesV2,
} from './router';
import { useAppSelector, useAppDispatch, loadCompanyUser as loadCompanyUserThunk } from './store';
import { HTTPInterceptor } from './services/axios/http-interceptors';
import { CompanyUser } from './types/companyUser';
import { COLORS, lightTheme } from './themev2';
import i18n from './i18n';
import MarIAWidget from './componentsv2/MarIAWidget';
import AcknowledgementModal from './componentsv2/AcknowledgementModal';
import { storeStatus } from './utils/common';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data is considered fresh for 5 minutes
      retry: 3, // Only retry failed requests 3 times
    },
  },
});

export default function App() {
  const { isAuth, user, isLoadingUser, isAdmin, isLender } = useAuth();
  const dispatch = useAppDispatch();
  const isLoadingCompanyUser = useAppSelector((state) => state.companyUser.status) === storeStatus.LOADING;
  const companyUser = useAppSelector((state) => state.companyUser.companyUser) || ({} as CompanyUser);
  const userPreferences = useAppSelector((state) => state.companyUser.companyUser?.user.preferences);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const zeroCompanies = useAppSelector((state) => state.companyUser.companyUser?.companies.length === 0);
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const requiresAcknowledgement =
    !selectedCompany?.isDraft && selectedCompany?.agreementSignatureDate === null;

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  useEffect(() => {
    // init amplitude
    const amplitudeKey = process.env.REACT_APP_AMPLITUDE_API_KEY;
    const recordUser = user && !user.isAdmin;
    if (amplitudeKey) {
      init(amplitudeKey);
      const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: recordUser ? 1 : 0,
      }) as Plugin;
      add(sessionReplayTracking);
    }
  }, [user]);

  useEffect(() => {
    // init user language
    if (userPreferences) {
      // Map 'en' to 'en-US' to match our i18next configuration
      const language = userPreferences.language === 'en' ? 'en-US' : userPreferences.language;
      i18n.changeLanguage(language);
    }
  }, [userPreferences]);

  useEffect(() => {
    if (user?.isAdmin) {
      // force english language for admin users
      i18n.changeLanguage('en-US');
    }
  }, [user]);

  useEffect(() => {
    if (user && !user.isAdmin && !user.isLender && !companyUser?.id) {
      dispatch(loadCompanyUserThunk())
        .unwrap()
        .then(({ selectedCompany }) => {
          if (selectedCompany) {
            const { uuid, country, name } = selectedCompany;
            const identifyObj = new Identify();
            identifyObj.set('Company ID', uuid);
            identifyObj.set('Country of Incorporation', country);
            identifyObj.set('Company name', name);
            identifyObj.set(`Company ${name}`, name);
            identifyObj.set(`Company ${name} ID`, uuid);
            identifyObj.set('User name', `${user.firstName} ${user.lastName}`);
            identifyObj.set('User email', user.email);
            identify(identifyObj);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* TODO: Try to eliminate this Suspense instance. */}
      <Suspense fallback={<SuspenseBackdrop componentRenderingIt="App.tsx" />}>
        <ThemeProvider theme={lightTheme}>
          <CssBaseline />
          <Router>
            <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
              <HTTPInterceptor />
              {isFirstRender || isLoadingUser || (!isAdmin && !isLender && isLoadingCompanyUser) ? (
                <SimpleBackdrop
                  isLoading={isFirstRender || isLoadingUser || (!isAdmin && !isLender && isLoadingCompanyUser)}
                  hasOpaqueBackground
                  componentRenderingIt="App.tsx"
                />
              ) : (
                <div
                  style={{
                    backgroundColor: COLORS.gray[200],
                    minHeight: '100vh',
                  }}
                >
                  <Routes>
                    {publicRoutesV2.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard isAdmin={isAdmin} isLender={isLender} isAuth={isAuth} isPublicRoute>
                            {route.component}
                          </AuthGuard>
                        }
                      />
                    ))}
                    {adminRoutesV2.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard isAdmin={isAdmin} isLender={isLender} isAuth={isAuth} isAdminRoute>
                            {route.component}
                          </AuthGuard>
                        }
                      />
                    ))}
                    {lenderRoutesV2.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard
                            isAdmin={isAdmin}
                            isLender={isLender}
                            isAuth={isAuth}
                            isLenderRoute
                            path={route.path}
                          >
                            {route.component}
                          </AuthGuard>
                        }
                      />
                    ))}
                    {companyUserRoutesV2.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard
                            isAdmin={isAdmin}
                            isLender={isLender}
                            isAuth={isAuth}
                            isCompanyUserRoute
                            zeroCompanies={zeroCompanies}
                            path={route.path}
                          >
                            {route.component}
                          </AuthGuard>
                        }
                      />
                    ))}
                    {allAccessibleRoutesV2.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard isAdmin={isAdmin} isAuth={isAuth} isAllAccessible>
                            {route.component}
                          </AuthGuard>
                        }
                      />
                    ))}
                    {standaloneRoutesV2.map((route) => (
                      <Route key={route.path} path={route.path} element={route.component} />
                    ))}
                    <Route path="/" element={<RootPage />} />
                  </Routes>
                  {isAuth && !isAdmin && !isLender && <MarIAWidget />}
                  {isAuth && !isAdmin && !isLender && requiresAcknowledgement && (
                    <AcknowledgementModal isOpen={requiresAcknowledgement} />
                  )}
                </div>
              )}
            </Sentry.ErrorBoundary>
          </Router>
        </ThemeProvider>
      </Suspense>
    </QueryClientProvider>
  );
}
